import { useState, useContext, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import moment from "moment-timezone"
import { enqueueSnackbar } from 'notistack'

import { Typography, Button, Link, Skeleton } from "@mui/material"

import FlareRoundedIcon from '@mui/icons-material/FlareRounded'

import { AppContext } from '/providers/app-context'
import { getPlans, cancelChangePlan, getUsage } from '/providers/studio-api'
import ChangePlanDialog from './change-plan-dialog'
import ApplyForMontlyContractDialog from './monthly-plan-application-form'
import StyledPaper from "../paper"

const Tariff = () => {

  const { apiSource, apiKey, setProcessing, client, refreshClient } = useContext(AppContext)
  const navigate = useNavigate()

  const [prepay, setPrepay] = useState([])
  const [postpay, setPostpay] = useState([])
  const [peakUsage, setPeakUsage] = useState(0)
  const [previousPeakUsage, setPreviousPeakUsage] = useState(0)
  const [currentUsage, setCurrentUsage] = useState(0)
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false)
  const [monthlyPlanApplicationOpen, setMonthlyPlanApplicationOpen] = useState(false)

  const nextBillingDate = useMemo(() => client ? getNextDateWithDay(client.billing_day_of_month).format("DD MMM YYYY") : "", [client])

  const currentPlanName = useMemo(() => {
    return client.tariff_tier
  }, [client])

  const nextPlanName = useMemo(() => {
    return client.next_tariff_tier
  }, [client])

  useEffect(() => {
    getPricePlans()
    getPassUsage()
  }, [])

  const getPricePlans = async () => {
    const result = await getPlans({ apiSource, apiKey })
    if (result.error) {
      enqueueSnackbar({ message: result.error.message ?? 'Error getting price plans', variant: 'error' })
    } else {
      setPrepay(result.prepay)
      setPostpay(result.postpay)
    }
  }

  const getPassUsage = async () => {
    const result = await getUsage({ apiSource, apiKey })
    if (result.error) {
      enqueueSnackbar({ message: result.error.message ?? 'Error getting current usage', variant: 'error' })
    } else {
      setPeakUsage(result.peak)
      setPreviousPeakUsage(result.previousPeak)
      setCurrentUsage(result.current)
    }
  }

  const changePlan = () => {
    setChangePlanDialogOpen(true)
  }

  const cancelChange = async () => {
    enqueueSnackbar({ message: 'Cancelling plan change', variant: 'light' })
    setProcessing(true)
    await cancelChangePlan({ apiSource, apiKey })
    setProcessing(false)
    enqueueSnackbar({ message: 'Plan change cancelled', variant: 'success' })
    refreshClient()
  }

  const applyForMonthlyContract = () => {
    setChangePlanDialogOpen(false)
    setMonthlyPlanApplicationOpen(true)
  }

  if (!client) return (
    <div className="flex flex-wrap gap-10 overflow-y-scroll p-5">
      <Skeleton variant="rounded" width={600} height={300} />
      <div className="flex flex-col gap-2 w-[500px] mr-20">
        <Skeleton variant="rounded" width={720} height={400} />
        <Skeleton variant="rounded" width={720} height={400} />
      </div>
    </div>
  )

  if (client?.billing_mode === "prepay") return (
    <>
      <div className="flex flex-wrap gap-5 justify-around w-full overflow-y-auto">
        <div className="flex flex-col gap-2 w-[500px]">
          <StyledPaper>

            <div className='flex flex-col gap-5 p-10'>

              <div>
                <div className="font-semibold">Plan Details</div>
                <ul className="list-disc ml-10">
                  <li>Current Plan: <span className="font-semibold text-xl">{currentPlanName}</span></li>
                  <li>Next Billing Date: <span className="font-semibold text-xl">{nextBillingDate}</span></li>
                  {client?.next_tariff_tier &&
                    <li><Typography color="secondary">You are scheduled to change to our <span className="font-semibold text-xl">{nextPlanName}</span> plan</Typography></li>
                  }
                </ul>

                <div className="font-semibold mt-5">Usage Overview</div>
                <ul className="list-disc ml-10">
                  <li>Active Passes Now: <span className="font-semibold text-xl">{currentUsage}</span></li>
                  <li>Peak Usage This Period: <span className="font-semibold text-xl">{peakUsage}</span> passes</li>
                  <li>Peak Usage Last Period: <span className="font-semibold text-xl">{previousPeakUsage}</span> passes</li>
                </ul>
              </div>

              <div className="flex flex-row justify-between mt-10">
                <Button variant="outlined" onClick={cancelChange} disabled={!client?.next_tariff_tier}>Cancel plan change plan</Button>
                <Button variant="contained" onClick={changePlan}>Change plan</Button>
              </div>

              <div className="italic text-xl mt-[50px]"><FlareRoundedIcon color="secondary" sx={{ fontSize: 40, marginRight: 2 }} />Complete an application to move to our <span className="font-semibold text-xl">monthly rolling plan</span> <Button onClick={applyForMonthlyContract} variant="outlined">Apply now</Button></div>
            </div>
          </StyledPaper>
        </div>

        <div className="flex flex-col gap-20 min-w-[720px] pb-20">
          <div className="flex flex-col gap-10 items-center">
            <StyledPaper>
              <div className='flex flex-col gap-5 p-10 w-full'>

                <Typography variant="h6">Pay-as-you-go plans</Typography>

                <div className="table w-full">
                  <div className="line font-bold">
                    <div className="align-bottom">Tier</div>
                    <div className="text-right">Active Passes</div>
                    <div className="align-bottom text-right"> Price/month (inc. VAT)</div>
                  </div>
                  {prepay.map(i =>
                    <div className="dotted" key={i.active_passes}>
                      <div >{i.tariff_tier}</div>
                      <div className="text-right">{i.active_passes}</div>
                      <div className="text-right">{i.price ? "£" + i.price : "FREE"}</div>
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-5">
                    <ul className="list-disc list-outside ml-5">
                      <li>All pay-as-you-go plans are billed in advance each calendar month via debit or credit card</li>
                      <li>You can change plans at any time.
                        <ul className="list-disc list-outside ml-10">
                          <li><Link href="https://www.smartix.uk/billing/change#upgrade">Upgrades</Link> apply immediately.</li>
                          <li><Link href="https://www.smartix.uk/billing/change#downgrade">Downgrades</Link> take effect at the start of your next billing period ({nextBillingDate})</li>
                        </ul>
                      </li>
                      <li>We will delete your payment details if you revert to the FREE plan</li>
                    </ul>
                  </div>
                </div>

              </div>
            </StyledPaper>

            <div className="flex flex-col gap-10 items-center ">
              <StyledPaper>

                <div className='flex flex-col gap-5 p-10'>

                  <Typography variant="h6">Standard monthly rolling plan</Typography>

                  <div className="table w-full">
                    <div className="line font-bold">
                      <div className="align-bottom">Band (active passes)</div>
                      <div className="align-bottom text-right">Price per active pass per month (ex. VAT)</div>
                    </div>
                    {getPostPayBands(postpay)}
                  </div>

                  <div>
                    <br />
                    <ul className="list-disc list-outside ml-5 mb-5">
                      <li>Our monthly rolling plan is billed <strong>in arrears</strong> each calendar month via direct debit</li>
                      <li>Payment is taken <strong>7 days after the invoice is raised</strong></li>
                      <li>Your <strong>peak usage</strong> during the month is recorded and billed according to the bands above</li>
                      <li>A <strong>minimum notice period of 3 months</strong> applies</li>
                      <li>A <strong>minimum monthly fee of £50</strong> applies</li>
                      <li>Our monthly plan is available to registered companies and charities</li>
                      <li>Approval is required for our monthly plan</li>
                      <li>We may carry out a credit check</li>
                      <li>Custom plans may be considered upon request</li>
                    </ul>

                  </div>
                </div>
              </StyledPaper>
            </div>
          </div>
        </div>
      </div>

      <ChangePlanDialog open={changePlanDialogOpen} setOpen={setChangePlanDialogOpen} client={client} prepay={prepay} applyForMonthlyContract={applyForMonthlyContract} />
      <ApplyForMontlyContractDialog open={monthlyPlanApplicationOpen} setOpen={setMonthlyPlanApplicationOpen} client={client} />
    </>
  )

  if (client?.billing_mode === "postpay") return (
    <div className="flex flex-wrap gap-10 overflow-y-scroll p-5 w-full pb-20">
      <StyledPaper>
        <div className="flex flex-col w-[500px] mr-20 items-center gap-20">
          <div className='flex flex-col gap-5 p-10'>
            <Typography variant="h6">You are using our <span className="font-semibold text-xl">montly rolling plan</span></Typography>
            {!client.stripe_dd_payment_method_id && <div>You must setup a direct debit mandate.</div>}
            <Button variant="contained" onClick={() => navigate("../direct-debit")}>{client.stripe_dd_payment_method_id ? "Change" : "Setup"} direct debit mandate</Button>
          </div>
        </div>
      </StyledPaper>
    </div>
  )
}

export default Tariff

const getPostPayBands = postpay => {

  let lowerBand = 1

  let bands = postpay.map(i => {

    const band = (
      <div className="dotted" key={i.band}>
        <div>{lowerBand === 1 ? "From" : "Then from"} {lowerBand.toLocaleString()} {i.band === -1 ? "+" : "to" + i.band.toLocaleString()}</div>
        <div className="text-right">{(i.price * 100) + "p"}</div>
      </div>
    )

    lowerBand = i.band + 1

    return band
  })

  return bands
}


const getNextDateWithDay = (dayNumber) => {
  const today = moment().startOf('day')

  let nextMonth = today.add(1, 'month')
  const daysInMonth = nextMonth.daysInMonth()

  // use the requested day if it exists, otherwise last day of the month
  const targetDay = Math.min(dayNumber, daysInMonth)

  return nextMonth.date(targetDay)
}